import React, { useState } from 'react';
import { z } from 'zod';
import { safeUrl } from '../url';
import { checkFormHygiene } from '../formHygiene';
import { LeadFormFields } from './leadForm';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Contact header — faithful rewrite of legacy molecules/ContactUsHero (Strapi
// sections.contact-header). A light band holding a dark navy info panel (title,
// description, addresses with icons, socials) on the left and a white lead-form
// card on the right. The form POSTs to the same-origin /api/leads endpoint
// (validates + proxies to Salesforce), like homeForm. Submit state via useState.
export const contactHeaderSchema = z.object({
  title: z.string().min(1),
  description: z.string().optional(),
  addresses: z
    .array(z.object({ iconUrl: z.string().optional(), text: z.string().min(1), url: safeUrl.optional() }))
    .default([]),
  socials: z.array(z.object({ iconUrl: z.string().optional(), link: safeUrl })).default([]),
});

type ContactHeaderProps = z.infer<typeof contactHeaderSchema>;
type Status = 'idle' | 'submitting' | 'success' | 'error';

const Band = styled.section`
  overflow: hidden;
  background: #bdc6db;

  @media (min-width: 64em) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: stretch;
  }
`;

const Info = styled.div`
  background: #012169;
  color: #ffffff;
  border-bottom-right-radius: 10rem;
  padding: 10rem 2rem 6rem;

  @media (min-width: 64em) {
    padding: 14rem 5rem;
    margin-left: auto;
    width: 100%;
    max-width: 64rem;
  }
`;

const Title = styled.h1`
  margin: 0;
  color: #ffffff;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 700;
  font-size: 2.5rem;

  @media (min-width: 48em) {
    font-size: 3.5rem;
  }
`;

const Description = styled.p`
  margin: 1.6rem 0 0;
  color: #ffffff;
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
  font-size: 1.6rem;
  line-height: 2.5rem;
  max-width: 32rem;

  @media (min-width: 48em) {
    margin-top: 3.8rem;
  }
`;

const AddressList = styled.div`
  margin-top: 5.6rem;
  display: grid;
  gap: 3rem;
`;

const Address = styled.div`
  display: grid;
  grid-template-columns: 2rem auto;
  gap: 1.6rem;
  align-items: start;
  color: #ffffff;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.6rem;
  line-height: 1.6;
`;

const AddrIcon = styled.img`
  width: 2rem;
  height: 2rem;
  object-fit: contain;
  margin-top: 0.3rem;
`;

const AddrLink = styled.a`
  color: #ffffff;
  text-decoration: none;
`;

const Socials = styled.div`
  display: flex;
  gap: 1.2rem;
  margin-top: 4rem;
`;

const Social = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 5rem;
  height: 5rem;
  background: #47d7ac;
  border-radius: 50%;
`;

const SocialIcon = styled.img`
  width: 2rem;
  height: 2rem;
  object-fit: contain;
`;

const FormWrap = styled.div`
  display: flex;
  align-items: center;
  padding: 2rem;

  @media (min-width: 64em) {
    padding: 4rem;
  }
`;

const FormCard = styled.form`
  display: grid;
  gap: 1.6rem;
  width: 100%;
  padding: 2.4rem;
  border-radius: 2.4rem;
  background: #ffffff;
  box-shadow: 0 2rem 5rem rgba(1, 33, 105, 0.15);

  @media (min-width: 64em) {
    padding: 3.2rem;
  }
`;

const Row = styled.div`
  display: grid;
  gap: 1.6rem;

  @media (min-width: 48em) {
    grid-template-columns: 1fr 1fr;
  }
`;

const fieldStyles = `
  width: 100%;
  box-sizing: border-box;
  padding: 1.3rem 1.6rem;
  border: 1px solid #d7deec;
  border-radius: 1rem;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.5rem;
  color: #0b1020;
  &:focus { outline: none; border-color: #012169; }
`;

const Input = styled.input`
  ${fieldStyles}
`;

const Textarea = styled.textarea`
  ${fieldStyles}
  min-height: 11rem;
  resize: vertical;
`;

const Honeypot = styled.input`
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
`;

const Submit = styled.button`
  appearance: none;
  padding: 1.4rem 2.4rem;
  border: none;
  border-radius: 1rem;
  background: #023eca;
  color: #ffffff;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.6rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Note = styled.p<{ $error?: boolean }>`
  margin: 0;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.4rem;
  color: ${({ $error }) => ($error ? '#c0362c' : '#1a7f37')};
`;

function ContactHeader({ title, description, addresses, socials }: ContactHeaderProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formEl = e.currentTarget;
    const payload = Object.fromEntries(new FormData(formEl).entries()) as Record<string, string>;
    if ((payload.message ?? '').trim().length < 40) {
      setStatus('error');
      setError('Message must be at least 40 characters.');
      return;
    }
    // Client-side XSS / profanity guard (server re-checks authoritatively).
    const hy = checkFormHygiene([payload.title, payload.company, payload.phone, payload.message], [payload.firstName, payload.lastName]);
    if (!hy.ok) {
      setStatus('error');
      setError(hy.reason === 'malicious' ? 'Your submission contains characters that are not allowed.' : 'Please remove the offensive language from your submission.');
      return;
    }
    setStatus('submitting');
    setError('');
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = (await res.json().catch(() => ({}))) as { ok?: boolean; message?: string };
      if (res.ok && data.ok) {
        setStatus('success');
        formEl.reset();
      } else {
        setStatus('error');
        setError(data.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setError('Network error. Please try again.');
    }
  }

  return (
    <Band>
      <Info>
        <Title>{title}</Title>
        {description ? <Description>{description}</Description> : null}
        {addresses.length > 0 ? (
          <AddressList>
            {addresses.map((a, i) => (
              <Address key={i}>
                {a.iconUrl ? <AddrIcon src={a.iconUrl} alt="" /> : <span />}
                {a.url ? <AddrLink href={a.url}>{a.text}</AddrLink> : <span>{a.text}</span>}
              </Address>
            ))}
          </AddressList>
        ) : null}
        {socials.length > 0 ? (
          <Socials>
            {socials.map((s, i) => (
              <Social key={i} href={s.link} target="_blank" rel="noreferrer" aria-label="Social">
                {s.iconUrl ? <SocialIcon src={s.iconUrl} alt="" /> : null}
              </Social>
            ))}
          </Socials>
        ) : null}
      </Info>

      <FormWrap>
        {/* Shared cross-tenant lead form (packages/blocks/leadForm.tsx). */}
        <LeadFormFields submitLabel="Submit" messageLabel="Message" />
      </FormWrap>
    </Band>
  );
}

export const contactHeaderBlock: BlockDefinition<ContactHeaderProps> = {
  type: 'contactHeader',
  schema: contactHeaderSchema,
  Renderer: ContactHeader,
};
